import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';

const SettingsPage = () => {
  const [stopLoss, setStopLoss] = useState(7); // Default 7%
  const [profitDropThreshold, setProfitDropThreshold] = useState(10); // Default 10%
  const [slippage, setSlippage] = useState(5); // Default 5%
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Load settings from backend
  const loadSettings = async () => {
    try {
      setLoading(true);
      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/settings`);
      const settings = response.data;
      
      setStopLoss(settings.stop_loss || 7);
      setProfitDropThreshold(settings.profit_drop_threshold || 10);
      setSlippage(settings.slippage || 5);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage('Failed to load settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  };

  // Save settings to backend
  const saveSettings = async () => {
    try {
      setSaving(true);
      setMessage('');
      const apiUrl = getApiUrl();
      
      await axios.post(`${apiUrl}/settings`, {
        stop_loss: stopLoss,
        profit_drop_threshold: profitDropThreshold,
        slippage: slippage
      });
      
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600 mb-2">
          Settings
        </h1>
        <p className="text-slate-300">Configure your trading parameters</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-slate-400">Loading settings...</p>
          </div>
        </div>
      ) : (
        <div className="w-full">
          {/* Trading Parameters Section */}
          <div className="card mb-6">
            <h2 className="text-2xl font-bold text-white mb-6">Trading Parameters</h2>
            
            {/* Current Values Display */}
            <div className="mb-8 backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
              <h3 className="text-lg font-semibold text-white mb-3">Current Settings</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-red-400">{stopLoss}%</div>
                  <div className="text-xs text-slate-400">Stop Loss</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">{profitDropThreshold}%</div>
                  <div className="text-xs text-slate-400">Peak Profit Drop</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-400">{slippage}%</div>
                  <div className="text-xs text-slate-400">Slippage</div>
                </div>
              </div>
            </div>
            
            {/* Stop Loss */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Stop Loss (%)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(Number(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer slider" style={{ backgroundColor: '#353535' }}
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(Number(e.target.value))}
                    className="w-20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none" style={{ backgroundColor: '#353535', border: '1px solid white' }}
                  />
                  <span className="text-slate-400">%</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Automatically sell when price drops by this percentage from buy price
              </p>
            </div>

            {/* Peak Profit Drop Threshold */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Peak Profit Drop Threshold (%)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="1"
                  value={profitDropThreshold}
                  onChange={(e) => setProfitDropThreshold(Number(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer slider" style={{ backgroundColor: '#353535' }}
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="5"
                    max="50"
                    step="1"
                    value={profitDropThreshold}
                    onChange={(e) => setProfitDropThreshold(Number(e.target.value))}
                    className="w-20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none" style={{ backgroundColor: '#353535', border: '1px solid white' }}
                  />
                  <span className="text-slate-400">%</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Take profit when peak profit amount drops by this percentage from its peak value
              </p>
            </div>
          </div>

          {/* Transaction Settings Section */}
          <div className="card mb-6">
            <h2 className="text-2xl font-bold text-white mb-6">Transaction Settings</h2>
            
            {/* Slippage */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Slippage Tolerance (%)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="0.1"
                  max="20"
                  step="0.1"
                  value={slippage}
                  onChange={(e) => setSlippage(Number(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer slider" style={{ backgroundColor: '#353535' }}
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0.1"
                    max="20"
                    step="0.1"
                    value={slippage}
                    onChange={(e) => setSlippage(Number(e.target.value))}
                    className="w-20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none" style={{ backgroundColor: '#353535', border: '1px solid white' }}
                  />
                  <span className="text-slate-400">%</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Maximum price difference acceptable during transaction execution
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="text-center">
            <button
              onClick={saveSettings}
              disabled={saving}
              className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-105 ${
                saving
                  ? 'cursor-not-allowed'
                  : 'shadow-lg hover:shadow-xl'
              }`}
              style={{ backgroundColor: saving ? '#353535' : '#353535' }}
              onMouseEnter={(e) => { if (!saving) e.target.style.backgroundColor = '#22c55e'; }}
              onMouseLeave={(e) => { if (!saving) e.target.style.backgroundColor = '#353535'; }}
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Settings'
              )}
            </button>
          </div>

          {/* Message */}
          {message && (
            <div className={`mt-4 p-4 rounded-lg text-center ${
              message.includes('success') || message.includes('saved')
                ? 'bg-green-900/50 border border-green-600 text-green-400'
                : 'bg-red-900/50 border border-red-600 text-red-400'
            }`}>
              {message}
            </div>
          )}

        </div>
      )}

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
};

export default SettingsPage;
